import { FC, useContext, useEffect, useState } from "react";
import Select, { GroupBase, OptionsOrGroups } from "react-select";
import { format, parse, subYears } from "date-fns";

import { CaseFilterProps } from "./types";
import ReactInputMask from "react-input-mask";
import DatePicker from "react-datepicker";
import { useForm, Controller } from "react-hook-form";
import { AuthContext } from "src/context/authContext";
import useFetch from "src/hooks/useFetch";
import { APIEndpoints } from "src/types/apiTypes";
import { QueryStateActions } from "src/pages/dashboard/types";

type FormValues = {
  open: string | boolean;
  date_from: string;
  date_to: string;
  search_query: string;
  practice_id: string[]; // ✅ array in form
  global_doctor_id: string;
};

const TableFilters: FC<CaseFilterProps> = ({
  state,
  dropdownData,
  inputPlaceholder,
  dispatch,
  handleSearchWithQueryParam,
  onClearButtonClicked,
  practiceDropdown,
}) => {

  const [startOpen, setStartOpen] = useState(false);
  const [endOpen, setEndOpen] = useState(false);

  const { userData } = useContext(AuthContext);

  // ✅ useForm setup
  const { control, handleSubmit, reset, watch, setValue } = useForm<FormValues>({
    defaultValues: {
      open: state.open,
      date_from: state.date_from || format((new Date()).setDate((new Date()).getDate() - 60), "yyyy-MM-dd"),
      date_to: state.date_to || format((new Date()).setDate((new Date()).getDate() + 1), "yyyy-MM-dd"),
      search_query: state.search_query,
      practice_id: state.practice_id
        ? state.practice_id.split(",")
        : [],
    },
  });

  useEffect(() => {
    if (!state) return;
    reset(state.practice_id
      ? {
        ...state,
        practice_id: state.practice_id.split(","),
      }
      : {
        ...state,
        practice_id: [],
      });
  }, [state, reset]);

  //✅ Sync form when state changes (important for back navigation)
  useEffect(() => {
    reset({
      open: state.open,
      date_from: state.date_from || format((new Date()).setDate((new Date()).getDate() - 60), "yyyy-MM-dd"),
      date_to: state.date_to || format((new Date()).setDate((new Date()).getDate() + 1), "yyyy-MM-dd"),
      search_query: state.search_query,
      practice_id: state.practice_id
        ? state.practice_id.split(",")
        : [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // ✅ Submit handler
  const onSubmit = (values: FormValues) => {
    const updated = {
      ...state,
      ...values,
      page: 1,
      practice_id: values.practice_id.join(","), // convert → API format
    };

    dispatch({
      type: QueryStateActions.CHANGE_QUERY_PARAMS,
      payload: updated,
    });

    handleSearchWithQueryParam(updated);
  };

  const { data: providersListing, mutate: getProvidersListing } =
    useFetch<{ id: number; name: string; }[]>(
        APIEndpoints.AD_PROVIDER_DROPDOWN, 
        {
          practice_id: state?.practice_id
            ? state.practice_id.split(",")
            : []
        },
        {},
        false
      );


  const clearFormData = () => {
    const values = {
      ...state,
      date_from: format(subYears(new Date(), 1), "yyyy-MM-dd"),
      date_to: format(new Date(), "yyyy-MM-dd"),
      ...(["Practice", "Contact"].includes(userData?.ref_type || "") ? 
        { practice_id: practiceDropdown?.[0]?.id.toString() ?? "" } : { practice_id: "" }
      ),
      case_id: "",
      global_patient_id: "",
    };

    reset({ ...values, practice_id: values.practice_id ? [values.practice_id] : [] });
    onClearButtonClicked(values as any);
    // updateQueryParams(values as any, true);
    // setHasAppliedFilters(false);
  };

  useEffect(() => {
    if (["Contact", "Practice"].includes(userData?.ref_type || "")) {
      if (practiceDropdown && practiceDropdown.length > 0) {
        setValue("practice_id", [practiceDropdown[0].id.toString()]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [practiceDropdown]);

  const practiceChange = (values: any) => {
    getProvidersListing({}, {}, { practice_id: values });
    setValue("global_doctor_id", "");
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="row g-2">

      {/* STATUS */}
      <div className="col">
        <Controller
          name="open"
          control={control}
          render={({ field }) => (
            <Select
              options={dropdownData}
              isSearchable={false}
              value={dropdownData.find(d => d.value === field.value)}
              onChange={(val: any) => field.onChange(val.value)}
            />
          )}
        />
      </div>

      {/* DATE FROM */}
      <div className="col">
        <Controller
          name="date_from"
          control={control}
          render={({ field }) => (
            <DatePicker
            className="form-control form-control-datepicker rounded-1" wrapperClassName="w-100"
              selected={
                field.value
                  ? parse(field.value, "yyyy-MM-dd", new Date())
                  : null
              }
              onChange={(date) =>
                field.onChange(date ? format(date, "yyyy-MM-dd") : "")
              }
              dateFormat="MM/dd/yyyy"
              customInput={
                <ReactInputMask mask="99/99/9999">
                  {(inputProps: any) => <input {...inputProps} />}
                </ReactInputMask>
              }
              maxDate={watch("date_to") ? new Date(watch("date_to")) : undefined}
              open={startOpen}
              onFocus={() => {
                setStartOpen(true);
                setEndOpen(false);
              }}
              onClickOutside={() => setStartOpen(false)}
              onSelect={() => setStartOpen(false)}
            />
          )}
        />
      </div>

      {/* DATE TO */}
      <div className="col">
        <Controller
          name="date_to"
          control={control}
          render={({ field }) => (
            <DatePicker
              selected={
                field.value
                  ? parse(field.value, "yyyy-MM-dd", new Date())
                  : null
              }
              onChange={(date) =>
                field.onChange(date ? format(date, "yyyy-MM-dd") : "")
              }
              dateFormat="MM/dd/yyyy"
className="form-control form-control-datepicker rounded-1" wrapperClassName="w-100"              customInput={
                <ReactInputMask mask="99/99/9999">
                  {(inputProps: any) => <input {...inputProps} />}
                </ReactInputMask>
              }
              minDate={watch("date_from") ? new Date(watch("date_from")) : undefined}
              open={endOpen}
              onFocus={() => {
                setStartOpen(false);
                setEndOpen(true);
              }}
              onClickOutside={() => setEndOpen(false)}
              onSelect={() => setEndOpen(false)}
            />
          )}
        />
      </div>

      {/* PRACTICE MULTI SELECT */}
     {["Organization"].includes(userData?.ref_type ?? "")  && <div className="col">
        <Controller
          name="practice_id"
          control={control}
          render={({ field }) => (
            <Select
              isMulti
              options={
                practiceDropdown?.map((item) => ({
                  label: item.name,
                  value: item.id.toString(),
                })) ?? []
              }
              isDisabled={["Contact", "Practice"].includes(userData?.ref_type || "")}
              value={field.value.map((val: string) => {
                const found = practiceDropdown?.find(
                  (item) => item.id.toString() === val
                );
                return found ? { label: found.name, value: val } : null;
              }).filter(Boolean)}
              onChange={(selected: any) => {
                field.onChange(selected?.map((s: any) => s.value) || []);
                if (selected && selected.length > 0) {
                  practiceChange(selected?.map((s: any) => s.value) || []);
                }
              }}
            />
          )}
        />
      </div>}
       {["Organization"].includes(userData?.ref_type ?? "") && (
        <div className="col">
                <Controller
                  name="global_doctor_id"
                  control={control}
                  defaultValue=""
                  render={({ field, fieldState: { error } }) => (
                    <Select
                      isClearable={true}
                      isSearchable={true}
                      options={(
                        providersListing &&
                          providersListing.length > 0 ?
                          providersListing.map((item) => ({
                            value: item.id.toString(),
                            label: item.name
                          }))
                          : []
                      ) as unknown as OptionsOrGroups<string, GroupBase<string>>}
                      id="filter_type"
                      {...field}
                      placeholder="Select Doctor..."
                      value={(() => {
                        if (field.value) {
                          const foundDoc = providersListing?.find((doc) => doc.id.toString() === ((field.value as unknown as { value: string; })?.value || field.value));
                          return foundDoc ? { label: foundDoc.name, value: foundDoc.id } : "";
                        } else
                          return "";
                      })()}
                      onChange={(val) => {
                        if (val) field.onChange(val)
                        else {
                          field.onChange("")
                          // if (hasAppliedFilters) {
                          //   searchForStats({ ...getValues(), global_doctor_id: "" });
                          // }
                        }
                      }}
                    />
                  )}
                />
              </div>)}

      {/* SEARCH INPUT */}
      <div className="col">
        <Controller
          name="search_query"
          control={control}
          render={({ field }) => (
            <input
              {...field}
              type="text"
              placeholder={inputPlaceholder}
              className="form-control"
            />
          )}
        />
      </div>

      {/* FIND */}
      <div className="col-6 col-md-2 col-lg-1">
        <button type="submit" className="btn btn-blue w-100">
          Find
        </button>
      </div>

      {/* CLEAR */}
      <div className="col-6 col-md-2 col-lg-1">
        <button
          type="button"
          onClick={clearFormData}
          className="btn btn-light w-100"
        >
          Clear
        </button>
      </div>

    </form>
  );
};

export default TableFilters;